import React, { useCallback, useMemo, useState } from 'react';
import Badge from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import ConfirmationDialog from '../../components/ui/ConfirmationDialog';
import SettingsHubPanel from '../../components/SettingsHubPanel';
import { PlusIcon, XIcon } from '../../components/ui/Icons';
import { Label } from '../../components/ui/Label';
import { Modal, ModalContent, ModalFooter, ModalHeader, ModalTitle } from '../../components/ui/Modal';
import { Select } from '../../components/ui/Select';
import { Switch } from '../../components/ui/Switch';
import { Textarea } from '../../components/ui/Textarea';
import TextInput from '../../components/ui/TextInput';
import { useNotification } from '../../contexts/NotificationContext';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import api from '../../services/api';
import type {
  AiModelConfiguration,
  AiModelConfigurationWritePayload,
  AiProviderId,
  EmbeddedInConfigurationHub,
} from './models';

const PROVIDER_OPTIONS: Array<{ value: AiProviderId; label: string }> = [
  { value: 'ollama', label: 'Ollama (local / Docker)' },
  { value: 'gemini', label: 'Gemini' },
  { value: 'openai', label: 'OpenAI' },
  { value: 'openai_compatible', label: 'OpenAI-compatible' },
];

const PROVIDER_FIELD_HINTS: Record<
  AiProviderId,
  {
    modelIdPlaceholder: string;
    labelPlaceholder: string;
    baseUrlPlaceholder: string;
    apiKeyRefPlaceholder: string;
    baseUrlHint?: string;
    apiKeyHint?: string;
  }
> = {
  ollama: {
    modelIdPlaceholder: 'llama3.1:8b',
    labelPlaceholder: 'Llama 3.1 8B (Ollama)',
    baseUrlPlaceholder: 'http://localhost:11434',
    apiKeyRefPlaceholder: 'Not required for Ollama',
    baseUrlHint: 'Host URL where Ollama listens. Docker on same machine: http://localhost:11434. Backend in Docker: http://ollama:11434 or http://host.docker.internal:11434.',
    apiKeyHint: 'Ollama does not use API keys — leave key fields empty.',
  },
  gemini: {
    modelIdPlaceholder: 'gemini-2.0-flash',
    labelPlaceholder: 'Gemini 2.0 Flash',
    baseUrlPlaceholder: 'Leave empty (Google API)',
    apiKeyRefPlaceholder: 'GEMINI_API_KEY or env:GEMINI_API_KEY',
  },
  openai: {
    modelIdPlaceholder: 'gpt-4o-mini',
    labelPlaceholder: 'GPT-4o mini',
    baseUrlPlaceholder: 'https://api.openai.com/v1',
    apiKeyRefPlaceholder: 'OPENAI_API_KEY or env:OPENAI_API_KEY',
  },
  openai_compatible: {
    modelIdPlaceholder: 'model-id-from-provider',
    labelPlaceholder: 'OpenRouter / compatible model',
    baseUrlPlaceholder: 'https://openrouter.ai/api/v1',
    apiKeyRefPlaceholder: 'OPENAI_API_KEY or env:OPENAI_API_KEY',
  },
};

function providerHints(provider: AiProviderId) {
  return PROVIDER_FIELD_HINTS[provider] ?? PROVIDER_FIELD_HINTS.gemini;
}

function applyProviderDefaults(provider: AiProviderId, editing: boolean): Partial<Draft> {
  if (editing) return { provider };
  const hints = providerHints(provider);
  if (provider === 'ollama') {
    return {
      provider,
      name: 'Ollama Llama 3.1',
      modelId: 'llama3.1:8b',
      label: hints.labelPlaceholder,
      baseUrl: 'http://localhost:11434',
      apiKeyRef: '',
      apiKeyInput: '',
    };
  }
  if (provider === 'gemini') {
    return {
      provider,
      name: 'Gemini Flash',
      modelId: hints.modelIdPlaceholder,
      label: hints.labelPlaceholder,
      baseUrl: '',
      apiKeyRef: 'GEMINI_API_KEY',
    };
  }
  return { provider };
}

function providerLabel(p: AiProviderId): string {
  return PROVIDER_OPTIONS.find((o) => o.value === p)?.label ?? p;
}

type Draft = {
  name: string;
  provider: AiProviderId;
  modelId: string;
  label: string;
  baseUrl: string;
  apiKeyRef: string;
  apiKeyInput: string;
  clearStoredApiKey: boolean;
  enabled: boolean;
  isDefault: boolean;
  temperature: string;
  maxTokens: string;
  inputPricePer1kUsd: string;
  outputPricePer1kUsd: string;
  notes: string;
};

const emptyDraft = (): Draft => ({
  name: '',
  provider: 'ollama',
  modelId: '',
  label: '',
  baseUrl: '',
  apiKeyRef: '',
  apiKeyInput: '',
  clearStoredApiKey: false,
  enabled: true,
  isDefault: false,
  temperature: '',
  maxTokens: '',
  inputPricePer1kUsd: '',
  outputPricePer1kUsd: '',
  notes: '',
});

function mapApiRow(raw: Record<string, unknown>): AiModelConfiguration {
  return {
    id: Number(raw.id),
    name: String(raw.name ?? ''),
    provider: raw.provider as AiProviderId,
    modelId: String(raw.modelId ?? ''),
    label: raw.label != null ? String(raw.label) : null,
    baseUrl: raw.baseUrl != null ? String(raw.baseUrl) : null,
    apiKeyRef: raw.apiKeyRef != null ? String(raw.apiKeyRef) : null,
    hasStoredApiKey: Boolean(raw.hasStoredApiKey),
    enabled: Boolean(raw.enabled),
    isDefault: Boolean(raw.isDefault),
    temperature: raw.temperature != null ? Number(raw.temperature) : null,
    maxTokens: raw.maxTokens != null ? Number(raw.maxTokens) : null,
    inputPricePer1kUsd:
      raw.inputPricePer1kUsd != null ? Number(raw.inputPricePer1kUsd) : null,
    outputPricePer1kUsd:
      raw.outputPricePer1kUsd != null ? Number(raw.outputPricePer1kUsd) : null,
    notes: raw.notes != null ? String(raw.notes) : null,
    createdAt: String(raw.createdAt ?? ''),
    updatedAt: String(raw.updatedAt ?? ''),
  };
}

function draftFromRow(row: AiModelConfiguration): Draft {
  return {
    name: row.name,
    provider: row.provider,
    modelId: row.modelId,
    label: row.label ?? '',
    baseUrl: row.baseUrl ?? '',
    apiKeyRef: row.apiKeyRef ?? '',
    apiKeyInput: '',
    clearStoredApiKey: false,
    enabled: row.enabled,
    isDefault: row.isDefault,
    temperature: row.temperature != null ? String(row.temperature) : '',
    maxTokens: row.maxTokens != null ? String(row.maxTokens) : '',
    inputPricePer1kUsd:
      row.inputPricePer1kUsd != null ? String(row.inputPricePer1kUsd) : '',
    outputPricePer1kUsd:
      row.outputPricePer1kUsd != null ? String(row.outputPricePer1kUsd) : '',
    notes: row.notes ?? '',
  };
}

const AiModelsSettings: React.FC<EmbeddedInConfigurationHub> = () => {
  const { colors } = useTheme();
  const { showSuccess, showError } = useNotification();
  const [rows, setRows] = useState<AiModelConfiguration[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState<Draft>(() => emptyDraft());
  const [editingId, setEditingId] = useState<number | null>(null);
  const [modelModalOpen, setModelModalOpen] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get<unknown[] | Record<string, unknown>>('/settings/ai-models');
      const list = Array.isArray(data) ? data : [];
      setRows(list.map((item) => mapApiRow(item as Record<string, unknown>)));
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        || (err as Error)?.message
        || 'Failed to load AI models';
      showError(msg);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [showError]);

  React.useEffect(() => {
    void loadRows();
  }, [loadRows]);

  const sortedRows = useMemo(
    () =>
      [...rows].sort((a, b) => {
        if (a.provider !== b.provider) return a.provider.localeCompare(b.provider);
        if (a.isDefault !== b.isDefault) return a.isDefault ? -1 : 1;
        return a.name.localeCompare(b.name);
      }),
    [rows],
  );

  const resetDraft = () => {
    setDraft(emptyDraft());
    setEditingId(null);
  };

  const closeModelModal = () => {
    if (saving) return;
    setModelModalOpen(false);
    resetDraft();
  };

  const openNewModel = () => {
    resetDraft();
    setDraft((p) => ({ ...p, ...applyProviderDefaults('ollama', false) }));
    setModelModalOpen(true);
  };

  const validateDraft = (): string | null => {
    if (!draft.name.trim()) return 'Model name is required.';
    if (!draft.modelId.trim()) return 'Model ID is required.';
    return null;
  };

  const buildPayload = (): AiModelConfigurationWritePayload => {
    const payload: AiModelConfigurationWritePayload = {
      name: draft.name.trim(),
      provider: draft.provider,
      modelId: draft.modelId.trim(),
      label: draft.label.trim() || undefined,
      baseUrl: draft.baseUrl.trim() || undefined,
      apiKeyRef: draft.apiKeyRef.trim() || undefined,
      enabled: draft.enabled,
      isDefault: draft.isDefault,
      notes: draft.notes.trim() || undefined,
    };
    const t = draft.temperature.trim();
    if (t) payload.temperature = Number(t);
    const m = draft.maxTokens.trim();
    if (m) payload.maxTokens = parseInt(m, 10);
    const inputPrice = draft.inputPricePer1kUsd.trim();
    if (inputPrice) payload.inputPricePer1kUsd = Number(inputPrice);
    const outputPrice = draft.outputPricePer1kUsd.trim();
    if (outputPrice) payload.outputPricePer1kUsd = Number(outputPrice);
    if (editingId != null) {
      if (draft.clearStoredApiKey) payload.apiKey = '';
      else if (draft.apiKeyInput.trim()) payload.apiKey = draft.apiKeyInput.trim();
    } else if (draft.apiKeyInput.trim()) {
      payload.apiKey = draft.apiKeyInput.trim();
    }
    return payload;
  };

  const handleSave = async () => {
    const err = validateDraft();
    if (err) {
      showError(err);
      return;
    }
    setSaving(true);
    try {
      const payload = buildPayload();
      if (editingId != null) {
        await api.patch(`/settings/ai-models/${editingId}`, payload);
        showSuccess('AI model updated.');
      } else {
        await api.post('/settings/ai-models', payload);
        showSuccess('AI model created.');
      }
      await loadRows();
      setModelModalOpen(false);
      resetDraft();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        || (err as Error)?.message
        || 'Save failed';
      showError(msg);
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (row: AiModelConfiguration) => {
    setEditingId(row.id);
    setDraft(draftFromRow(row));
    setModelModalOpen(true);
  };

  const removeRow = async (id: number) => {
    try {
      await api.delete(`/settings/ai-models/${id}`);
      showSuccess('AI model removed.');
      if (editingId === id) {
        setModelModalOpen(false);
        resetDraft();
      }
      await loadRows();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        || (err as Error)?.message
        || 'Delete failed';
      showError(msg);
    }
  };

  const handleDeleteConfirm = async () => {
    const id = deleteConfirmId;
    if (id == null) return;
    setDeleteConfirmId(null);
    await removeRow(id);
  };

  return (
    <div className="space-y-6">
      <SettingsHubPanel
        title="AI models"
        badge={
          !loading ? (
            <span
              className={cn(
                'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium tabular-nums',
                colors.textMuted,
                'bg-black/[0.06] dark:bg-white/[0.08]',
              )}
            >
              {sortedRows.length} {sortedRows.length === 1 ? 'model' : 'models'}
            </span>
          ) : undefined
        }
        description={
          <>
            Configurations used by the Email AI drawer and other AI features. Enabled models appear in the in-app
            picker; one default per provider is pre-selected when the drawer opens.
          </>
        }
        helpItems={[
          {
            summary: 'Server configuration',
            content: (
              <p>
                Stored API keys are encrypted with{' '}
                <code className="rounded px-1 font-mono text-[11px]">EMAIL_ENCRYPTION_KEY</code>. Set{' '}
                <code className="rounded px-1 font-mono text-[11px]">AI_PROVIDER</code> on the server to match the
                provider family (Gemini, OpenAI, Ollama) so routing resolves correctly.
              </p>
            ),
          },
        ]}
        headerActions={
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className={cn('p-2 rounded-lg shadow-sm', colors.textMuted, colors.interactiveHover)}
            title="New AI model"
            aria-label="New AI model"
            onClick={() => openNewModel()}
          >
            <PlusIcon className="h-5 w-5" />
          </Button>
        }
      >
          {loading ? (
            <div className="space-y-3" role="status" aria-label="Loading models">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className={cn(
                    'h-[5.5rem] animate-pulse rounded-2xl',
                    'bg-slate-200/70 dark:bg-slate-700/50',
                  )}
                />
              ))}
            </div>
          ) : sortedRows.length === 0 ? (
            <div
              className={cn(
                'flex flex-col items-center justify-center rounded-2xl border border-dashed px-6 py-14 text-center',
                colors.border,
                'bg-black/[0.02] dark:bg-white/[0.02]',
              )}
            >
              <div
                className={cn(
                  'flex h-14 w-14 items-center justify-center rounded-2xl',
                  'bg-gradient-to-br from-indigo-500/15 to-violet-500/10 text-indigo-600 dark:text-indigo-400',
                )}
              >
                <svg className="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z"
                  />
                </svg>
              </div>
              <p className={cn('mt-5 text-sm font-semibold', colors.text)}>No AI models yet</p>
              <p className={cn('mt-1 max-w-sm text-sm', colors.textMuted)}>
                Add a model with the + button above. Gemini, OpenAI, OpenAI-compatible, and Ollama are supported.
              </p>
            </div>
          ) : (
            <div className="space-y-2 md:space-y-0 md:rounded-xl md:border md:overflow-hidden">
              <ul className="space-y-2 md:hidden">
                {sortedRows.map((row) => {
                  const isActive = modelModalOpen && editingId === row.id;
                  return (
                    <li key={row.id}>
                      <div
                        className={cn(
                          'relative overflow-hidden rounded-2xl border p-4 shadow-sm transition-shadow',
                          colors.border,
                          colors.surface,
                          isActive &&
                            'ring-2 ring-indigo-500/50 ring-offset-2 ring-offset-white dark:ring-offset-slate-950',
                        )}
                      >
                        <div className="absolute left-0 top-0 h-full w-1 bg-gradient-to-b from-indigo-500 to-violet-500 opacity-90" />
                        <div className="pl-3">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <p className={cn('font-semibold leading-tight', colors.text)}>{row.name}</p>
                              <code
                                className={cn(
                                  'mt-0.5 block truncate font-mono text-[11px]',
                                  colors.textMuted,
                                )}
                                title={row.modelId}
                              >
                                {row.modelId}
                              </code>
                              {row.label && (
                                <p className={cn('mt-1 text-xs', colors.textMuted)}>{row.label}</p>
                              )}
                              {row.notes && (
                                <p
                                  className={cn('mt-2 line-clamp-2 text-[11px] leading-snug', colors.textMuted)}
                                  title={row.notes}
                                >
                                  {row.notes.replace(/\s+/g, ' ').trim()}
                                </p>
                              )}
                            </div>
                            <div className="flex shrink-0 flex-col items-end gap-1">
                              <Badge
                                variant={row.enabled ? 'success' : 'secondary'}
                                className="rounded-md text-[10px]"
                              >
                                {row.enabled ? 'On' : 'Off'}
                              </Badge>
                            </div>
                          </div>
                          <div className="mt-3 flex flex-wrap items-center gap-1.5">
                            <span
                              className={cn(
                                'inline-flex items-center rounded-md px-2 py-0.5 text-[10px] font-medium',
                                'bg-sky-500/10 text-sky-800 dark:bg-sky-400/15 dark:text-sky-200',
                              )}
                            >
                              {providerLabel(row.provider)}
                            </span>
                            {row.isDefault && (
                              <span
                                className={cn(
                                  'inline-flex items-center rounded-md px-2 py-0.5 text-[10px] font-medium',
                                  'bg-violet-500/10 text-violet-800 dark:bg-violet-400/15 dark:text-violet-200',
                                )}
                              >
                                Default
                              </span>
                            )}
                            {row.hasStoredApiKey && (
                              <span
                                className={cn(
                                  'inline-flex items-center rounded-md px-2 py-0.5 text-[10px] font-medium',
                                  'bg-black/[0.06] dark:bg-white/[0.08]',
                                  colors.textMuted,
                                )}
                              >
                                Key stored
                              </span>
                            )}
                            {row.apiKeyRef && !row.hasStoredApiKey && (
                              <span className={cn('text-[10px]', colors.textMuted)}>Env ref</span>
                            )}
                          </div>
                          <div className="mt-3 flex justify-end gap-1 border-t border-black/5 pt-3 dark:border-white/10">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className={cn('p-2 rounded-lg', colors.textMuted, colors.interactiveHover)}
                              title="Edit"
                              aria-label="Edit model"
                              onClick={() => startEdit(row)}
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                />
                              </svg>
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="p-2 rounded-lg text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40"
                              title="Delete"
                              aria-label={`Delete ${row.name}`}
                              onClick={() => setDeleteConfirmId(row.id)}
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                />
                              </svg>
                            </Button>
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>

              <div className="hidden md:block">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className={cn('border-b text-xs uppercase tracking-wide', colors.border, colors.textMuted)}>
                      <th className="w-[120px] px-4 py-3 font-semibold" scope="col">
                        Provider
                      </th>
                      <th className="min-w-[180px] px-4 py-3 font-semibold" scope="col">
                        Model
                      </th>
                      <th className="min-w-[140px] px-4 py-3 font-semibold" scope="col">
                        Model ID
                      </th>
                      <th className="w-[120px] px-4 py-3 font-semibold" scope="col">
                        Status
                      </th>
                      <th className="w-[100px] px-4 py-3 font-semibold" scope="col">
                        API key
                      </th>
                      <th className="w-20 px-4 py-3 text-right font-semibold" scope="col">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className={cn('divide-y', colors.border)}>
                    {sortedRows.map((row) => {
                      const isActive = modelModalOpen && editingId === row.id;
                      return (
                        <tr
                          key={row.id}
                          className={cn(
                            'transition-colors',
                            isActive && 'bg-indigo-500/[0.07] dark:bg-indigo-400/[0.09]',
                            !isActive && 'hover:bg-black/[0.02] dark:hover:bg-white/[0.03]',
                          )}
                        >
                          <td className="px-4 py-3 align-top">
                            <span
                              className={cn(
                                'inline-flex w-fit items-center rounded-md px-2 py-0.5 text-[11px] font-medium',
                                'bg-sky-500/10 text-sky-800 dark:bg-sky-400/15 dark:text-sky-200',
                              )}
                            >
                              {providerLabel(row.provider)}
                            </span>
                          </td>
                          <td className="px-4 py-3 align-top">
                            <p className={cn('font-semibold leading-snug', colors.text)}>{row.name}</p>
                            {row.label && (
                              <p className={cn('mt-0.5 text-xs', colors.textMuted)}>{row.label}</p>
                            )}
                            {row.notes && (
                              <p
                                className={cn('mt-1.5 line-clamp-2 text-[11px] leading-relaxed', colors.textMuted)}
                                title={row.notes}
                              >
                                {row.notes.replace(/\s+/g, ' ').trim()}
                              </p>
                            )}
                          </td>
                          <td className="px-4 py-3 align-top">
                            <code className={cn('font-mono text-xs break-all', colors.textMuted)}>{row.modelId}</code>
                          </td>
                          <td className="px-4 py-3 align-top">
                            <div className="flex flex-col gap-1">
                              <Badge variant={row.enabled ? 'success' : 'secondary'} className="w-fit rounded-md text-[11px]">
                                {row.enabled ? 'Enabled' : 'Disabled'}
                              </Badge>
                              {row.isDefault && (
                                <span
                                  className={cn(
                                    'inline-flex w-fit items-center rounded-md px-2 py-0.5 text-[10px] font-medium',
                                    'bg-violet-500/10 text-violet-800 dark:text-violet-300',
                                  )}
                                >
                                  Default
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 align-top text-xs">
                            {row.hasStoredApiKey ? (
                              <span className={cn('font-medium', colors.text)}>Stored</span>
                            ) : row.apiKeyRef ? (
                              <span className={cn(colors.textMuted)}>Ref only</span>
                            ) : (
                              <span className={cn('opacity-60', colors.textMuted)}>—</span>
                            )}
                          </td>
                          <td className="px-4 py-3 align-top text-right">
                            <div className="inline-flex items-center justify-end gap-0.5">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className={cn('p-2 rounded-lg', colors.textMuted, colors.interactiveHover)}
                                title="Edit"
                                aria-label="Edit model"
                                onClick={() => startEdit(row)}
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                  />
                                </svg>
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="p-2 rounded-lg text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40"
                                title="Delete"
                                aria-label={`Delete ${row.name}`}
                                onClick={() => setDeleteConfirmId(row.id)}
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                  />
                                </svg>
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
      </SettingsHubPanel>

      <Modal
        isOpen={modelModalOpen}
        onClose={closeModelModal}
        size="xl"
        closeOnEscape={!saving}
      >
        <ModalHeader>
          <div className="flex w-full min-w-0 items-center justify-between gap-3">
            <ModalTitle>{editingId != null ? 'Edit AI model' : 'New AI model'}</ModalTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="shrink-0 rounded-lg p-2"
              aria-label="Close"
              disabled={saving}
              onClick={closeModelModal}
            >
              <XIcon className="h-5 w-5" />
            </Button>
          </div>
        </ModalHeader>
        <form
          className="flex min-h-0 flex-1 flex-col overflow-hidden"
          onSubmit={(e) => {
            e.preventDefault();
            void handleSave();
          }}
        >
          <ModalContent>
            <p className={cn('text-sm mb-6', colors.textMuted)}>
              Fields map to the server provider integration. Supported providers:{' '}
              <strong>Ollama</strong> (local/Docker), Gemini, OpenAI, OpenAI-compatible. Use API key reference
              for env-based secrets; paste a key to store it encrypted when you cannot rely on environment variables.
            </p>
            {(() => {
              const hints = providerHints(draft.provider);
              return (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="ai-model-name" required className="mb-1">
                  Name
                </Label>
                <TextInput
                  id="ai-model-name"
                  value={draft.name}
                  onChange={(e) => setDraft((p) => ({ ...p, name: e.target.value }))}
                  placeholder="Gemini Flash (Free)"
                  className="mt-1"
                  required
                />
              </div>
              <div>
                <Label className="mb-1">Provider</Label>
                <Select
                  value={draft.provider}
                  onChange={(e) => {
                    const next = e.target.value as AiProviderId;
                    setDraft((p) => ({ ...p, ...applyProviderDefaults(next, editingId != null) }));
                  }}
                  options={PROVIDER_OPTIONS}
                  placeholder="Select provider"
                  showClearButton={false}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="ai-model-model-id" required className="mb-1">
                  Model ID
                </Label>
                <TextInput
                  id="ai-model-model-id"
                  value={draft.modelId}
                  onChange={(e) => setDraft((p) => ({ ...p, modelId: e.target.value }))}
                  placeholder={hints.modelIdPlaceholder}
                  className="mt-1 font-mono text-sm"
                  required
                />
              </div>
              <div>
                <Label htmlFor="ai-model-label" className="mb-1">
                  Display label
                </Label>
                <TextInput
                  id="ai-model-label"
                  value={draft.label}
                  onChange={(e) => setDraft((p) => ({ ...p, label: e.target.value }))}
                  placeholder={hints.labelPlaceholder}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="ai-model-base-url" className="mb-1">
                  Base URL (optional)
                </Label>
                <TextInput
                  id="ai-model-base-url"
                  value={draft.baseUrl}
                  onChange={(e) => setDraft((p) => ({ ...p, baseUrl: e.target.value }))}
                  placeholder={hints.baseUrlPlaceholder}
                  className="mt-1"
                />
                {hints.baseUrlHint ? (
                  <p className={cn('mt-1 text-xs', colors.textMuted)}>{hints.baseUrlHint}</p>
                ) : null}
              </div>
              <div>
                <Label htmlFor="ai-model-api-key-ref" className="mb-1">
                  API key reference (optional)
                </Label>
                <TextInput
                  id="ai-model-api-key-ref"
                  value={draft.apiKeyRef}
                  onChange={(e) => setDraft((p) => ({ ...p, apiKeyRef: e.target.value }))}
                  placeholder={hints.apiKeyRefPlaceholder}
                  className="mt-1 font-mono text-sm"
                  disabled={draft.provider === 'ollama'}
                />
                <p className={cn('mt-1 text-xs', colors.textMuted)}>
                  {hints.apiKeyHint ?? 'Reads from environment at runtime (no secret stored in DB).'}
                </p>
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="ai-model-api-key" className="mb-1">
                  API key (optional, stored encrypted)
                </Label>
                <TextInput
                  id="ai-model-api-key"
                  type="password"
                  autoComplete="new-password"
                  value={draft.apiKeyInput}
                  onChange={(e) => setDraft((p) => ({ ...p, apiKeyInput: e.target.value }))}
                  placeholder={editingId != null ? 'Leave blank to keep current key' : 'Paste API key'}
                  className="mt-1"
                  disabled={draft.provider === 'ollama'}
                />
                {draft.provider === 'ollama' ? (
                  <p className={cn('mt-1 text-xs', colors.textMuted)}>Not used for Ollama.</p>
                ) : null}
                {editingId != null && (
                  <div className={cn('mt-3 flex items-center justify-between p-4 border rounded-xl', colors.border)}>
                    <div className="flex-1 pr-4">
                      <Label htmlFor="ai-model-clear-key" className="mb-1">
                        Clear stored key
                      </Label>
                      <p className={cn('text-xs', colors.textMuted)}>
                        Remove the encrypted key from the database for this model.
                      </p>
                    </div>
                    <Switch
                      id="ai-model-clear-key"
                      checked={draft.clearStoredApiKey}
                      onChange={(e) =>
                        setDraft((p) => ({
                          ...p,
                          clearStoredApiKey: e.target.checked,
                          apiKeyInput: e.target.checked ? '' : p.apiKeyInput,
                        }))
                      }
                    />
                  </div>
                )}
              </div>
              <div>
                <Label htmlFor="ai-model-temperature" className="mb-1">
                  Temperature
                </Label>
                <TextInput
                  id="ai-model-temperature"
                  type="number"
                  value={draft.temperature}
                  onChange={(e) => setDraft((p) => ({ ...p, temperature: e.target.value }))}
                  placeholder="0.2"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="ai-model-max-tokens" className="mb-1">
                  Max tokens
                </Label>
                <TextInput
                  id="ai-model-max-tokens"
                  type="number"
                  value={draft.maxTokens}
                  onChange={(e) => setDraft((p) => ({ ...p, maxTokens: e.target.value }))}
                  placeholder="1024"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="ai-model-input-price" className="mb-1">
                  Input price (USD / 1K tokens)
                </Label>
                <TextInput
                  id="ai-model-input-price"
                  type="number"
                  step="0.0001"
                  min="0"
                  value={draft.inputPricePer1kUsd}
                  onChange={(e) => setDraft((p) => ({ ...p, inputPricePer1kUsd: e.target.value }))}
                  placeholder="0.00015"
                  className="mt-1"
                />
                <p className={cn('mt-1 text-xs', colors.textMuted)}>
                  For usage cost estimates. Example: GPT-4o mini input ≈ 0.00015.
                </p>
              </div>
              <div>
                <Label htmlFor="ai-model-output-price" className="mb-1">
                  Output price (USD / 1K tokens)
                </Label>
                <TextInput
                  id="ai-model-output-price"
                  type="number"
                  step="0.0001"
                  min="0"
                  value={draft.outputPricePer1kUsd}
                  onChange={(e) => setDraft((p) => ({ ...p, outputPricePer1kUsd: e.target.value }))}
                  placeholder="0.0006"
                  className="mt-1"
                />
                <p className={cn('mt-1 text-xs', colors.textMuted)}>
                  Leave empty for local Ollama models (no API cost).
                </p>
              </div>
            </div>
              );
            })()}

            <div className="mt-5">
              <Label htmlFor="ai-model-notes" className="mb-1">
                Notes
              </Label>
              <Textarea
                id="ai-model-notes"
                value={draft.notes}
                onChange={(e) => setDraft((p) => ({ ...p, notes: e.target.value }))}
                rows={3}
                placeholder="Provider or account notes…"
                className="mt-1"
              />
            </div>

            <div className="mt-6 space-y-3">
              <div className={cn('flex items-center justify-between p-4 border rounded-xl', colors.border)}>
                <div className="flex-1 pr-4">
                  <Label htmlFor="ai-model-enabled" className="mb-1">
                    Enabled
                  </Label>
                  <p className={cn('text-xs', colors.textMuted)}>
                    When off, this model is hidden from the AI picker and cannot be used.
                  </p>
                </div>
                <Switch
                  id="ai-model-enabled"
                  checked={draft.enabled}
                  onChange={(e) => setDraft((p) => ({ ...p, enabled: e.target.checked }))}
                />
              </div>
              <div className={cn('flex items-center justify-between p-4 border rounded-xl', colors.border)}>
                <div className="flex-1 pr-4">
                  <Label htmlFor="ai-model-default" className="mb-1">
                    Default for this provider
                  </Label>
                  <p className={cn('text-xs', colors.textMuted)}>
                    Pre-selected in the AI drawer when it opens (only one default per provider).
                  </p>
                </div>
                <Switch
                  id="ai-model-default"
                  checked={draft.isDefault}
                  onChange={(e) => setDraft((p) => ({ ...p, isDefault: e.target.checked }))}
                />
              </div>
            </div>
          </ModalContent>
          <ModalFooter className="justify-end gap-2 sm:gap-3">
            <Button type="button" variant="ghost" disabled={saving} onClick={closeModelModal}>
              Cancel
            </Button>
            <Button type="submit" variant="default" disabled={saving || loading}>
              {saving ? 'Saving…' : editingId != null ? 'Save changes' : 'Create model'}
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      <ConfirmationDialog
        isOpen={deleteConfirmId != null}
        onClose={() => setDeleteConfirmId(null)}
        onConfirm={() => void handleDeleteConfirm()}
        title="Delete model"
        message="Delete this model configuration?"
        confirmText="Delete"
        confirmVariant="danger"
      />
    </div>
  );
};

export default AiModelsSettings;
